import React from 'react';
import { router } from '@inertiajs/react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtPriceForSymbol, fmtDate } from './DcaUtils';
import { Clock, TrendingUp, Zap } from "lucide-react";

interface LadderViewProps {
  activeDetails: any[];
  leverage: number;
  curSym: string;
  botId?: number;
}

export const LadderView: React.FC<LadderViewProps> = ({ activeDetails, leverage, curSym, botId }) => {
  if (activeDetails.length === 0) {
    return <div className="p-12 text-center text-muted-foreground/40 text-xs font-bold">활성화된 사이클이 없습니다.</div>;
  }

  const handleForceScaling = (side: string) => {
    if (!botId) return;
    if (window.confirm(`현재 가격으로 즉시 시장가 ${side.toUpperCase()} 추매(물타기)를 실행하시겠습니까?\n(다음 대기중인 사다리 차수 주문이 체결됩니다.)`)) {
      router.post(`/dca/${botId}/force_scaling`, { side }, {
        preserveScroll: true
      });
    }
  };

  return (
    <div className="divide-y divide-border">
      {activeDetails.map((c: any) => {
        const firstPendingTarget = (c.targets || []).find((t: any) => t.status !== 'filled' && t.status !== 'failed');
        
        return (
          <div key={c.id} className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className={`text-[10px] font-black uppercase tracking-widest ${c.side === 'long' ? 'text-success' : 'text-danger'}`}>
                {c.side} Ladder Targets
              </p>
              <Badge variant="outline" className="text-[9px] font-bold h-4 border-border text-muted-foreground">Step {c.filled_steps} Active</Badge>
            </div>

            <div className="space-y-1.5">
              {/* Header Row */}
              <div className="grid grid-cols-[80px_1.2fr_1fr] md:grid-cols-[100px_1fr_1fr] gap-2 px-2.5 pb-1 text-[9px] font-black text-muted-foreground/50 uppercase tracking-tight">
                <div className="flex items-center gap-2">차수</div>
                <div className="text-left md:text-center">실체결가격 / 예정가격</div>
                <div className="text-right">비용 / 체결시간 / 평단</div>
              </div>

              {(c.targets || []).map((t: any) => {
                const isFirstPending = t.id === firstPendingTarget?.id;
                
                return (
                  <div key={t.id} className={`p-2.5 rounded-xl border grid grid-cols-[80px_1.2fr_1fr] md:grid-cols-[100px_1fr_1fr] gap-2 items-center group relative ${t.status === 'filled' ? (c.side === 'long' ? 'bg-success/[0.03] border-success/20' : 'bg-danger/[0.03] border-danger/20') : (t.status === 'failed' ? 'bg-danger/[0.03] border-danger/20' : 'bg-background border-border')}`}>
                    {/* Column 1: Step & Status */}
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-black ${t.status === 'filled' ? (c.side === 'long' ? 'bg-success text-white' : 'bg-danger text-white') : (t.status === 'failed' ? 'bg-danger text-white' : 'bg-muted text-muted-foreground/60')}`}>{t.step_number}</div>
                      
                      {isFirstPending && botId ? (
                        <div
                          className="relative flex items-center h-6"
                          onBlur={(e) => {
                            // 다른 곳 탭하면 버튼 닫힘
                            const btn = e.currentTarget.querySelector('button.hidden');
                            if (!btn) {
                              const visibleBtn = e.currentTarget.querySelector('button:not(.hidden)');
                              if (visibleBtn) visibleBtn.classList.add('hidden');
                            }
                          }}
                        >
                          <span
                            className="text-[10px] font-bold text-muted-foreground/40 group-hover:hidden transition-all cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              const btn = e.currentTarget.parentElement?.querySelector('button');
                              if (!btn) return;
                              const isHidden = btn.classList.contains('hidden');
                              if (isHidden) {
                                btn.classList.remove('hidden');
                              } else {
                                handleForceScaling(c.side);
                              }
                            }}
                          >
                            PENDING
                          </span>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleForceScaling(c.side);
                            }}
                            className="hidden group-hover:flex h-5 px-1.5 text-[9px] font-black bg-amber-500 hover:bg-amber-600 text-white rounded-md shadow-sm items-center gap-0.5 transition-all border-none"
                          >
                            <Zap className="w-2.5 h-2.5 fill-current" />
                            시장가 추매
                          </Button>
                        </div>
                      ) : (
                        <span className={`text-[10px] font-bold ${t.status === 'filled' ? 'text-foreground' : (t.status === 'failed' ? 'text-danger' : 'text-muted-foreground/40')}`}>
                          {t.status === 'filled' ? 'FILLED' : (t.status === 'failed' ? 'FAILED' : 'PENDING')}
                        </span>
                      )}
                    </div>

                    {/* Column 2: Price Information */}
                    <div className="text-left md:text-center leading-tight">
                      <p className={`text-xs font-black ${t.status === 'filled' ? 'text-foreground' : 'text-muted-foreground/70'}`}>
                        {curSym}{fmtPriceForSymbol(Number(t.status === 'filled' ? (t.filled_price || t.target_price) : t.target_price), c.symbol)}
                      </p>
                      {t.status === 'filled' && t.filled_price && Math.abs(Number(t.filled_price) - Number(t.target_price)) > 0.00000001 && (
                        <p className="text-[9px] font-bold text-primary/70">
                          / {curSym}{fmtPriceForSymbol(Number(t.target_price), c.symbol)}
                        </p>
                      )}
                    </div>

                    {/* Column 3: Execution Details & Stats */}
                    <div className="text-right flex flex-col items-end gap-0.5">
                      <p className={`text-[10px] font-black ${t.status === 'filled' ? 'text-foreground' : 'text-muted-foreground/30'}`}>
                        {curSym}{Number(t.target_qty * (t.status === 'filled' ? (t.filled_price || t.target_price) : t.target_price) / leverage).toFixed(2)}
                      </p>
                      {t.status === 'filled' ? (
                        <>
                          {t.filled_at && (
                            <p className="text-[9px] font-medium text-muted-foreground/70">
                              <Clock className="w-2.5 h-2.5 inline-block" /> {fmtDate(t.filled_at).slice(5)}
                            </p>
                          )}
                          {t.avg_price_after_fill && (
                            <p className={`text-[9px] font-black flex items-center gap-1 ${c.side === 'long' ? 'text-success/80' : 'text-danger/80'}`}>
                              <TrendingUp className={`w-2.5 h-2.5 ${c.side === 'short' ? 'rotate-180' : ''}`} />
                              {curSym}{fmtPriceForSymbol(t.avg_price_after_fill, c.symbol)}
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="text-[9px] font-medium text-muted-foreground/20 italic">Waiting...</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
